import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    Tabs,
    Tab,
    Box,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { lookupApi, LookupItem } from '../services/lookupApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Lookups: React.FC = () => {
    const { t } = useLanguage();
    
    
    type TabKey = 'proprietaires' | 'fuelTypes' | 'affectations' | 'docTypes' | 'mntTypes';
    const [activeTab, setActiveTab] = useState<TabKey>('proprietaires');
    const [items, setItems] = useState<LookupItem[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<LookupItem | null>(null);
    const [formData, setFormData] = useState({
        code: '',
        lib: ''
    });

    const getApiForTab = (tab: TabKey) => {
        return lookupApi[tab];
    };

    const loadItems = async (tab: TabKey = activeTab) => {
        try {
            const api = getApiForTab(tab);
            const response = await api.getAll();
            if (response.success) {
                setItems(response.data);
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Failed to load lookup items:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load lookup items');
        }
    };

    useEffect(() => {
        loadItems(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: TabKey) => {
        setActiveTab(newValue);
    };

    const handleOpenDialog = (item?: LookupItem) => {
        if (item) {
            setEditingItem(item);
            setFormData({ code: item.code, lib: item.lib });
        } else {
            setEditingItem(null);
            setFormData({ code: '', lib: '' });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.code || !formData.lib) {
            blobticMessage.error(t('common.messages.warning') || 'Warning', t('zoom.check.fillRequired'));
            return;
        }

        try {
            const api = getApiForTab(activeTab);
            let response;
            if (editingItem) {
                response = await api.update(editingItem.id, formData);
            } else {
                response = await api.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setIsDialogOpen(false);
                loadItems();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving lookup item:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.confirm.delete') || 'Are you sure?')) return;
        try {
            const api = getApiForTab(activeTab);
            const response = await api.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadItems();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.delete_error'));
        }
    };

    const columns = [
        { id: 'code', label: t('page.parameters.table.code') || 'Code', minWidth: 150 },
        { id: 'lib', label: t('page.parameters.table.label') || 'Libellé', minWidth: 250 }
    ];

    const getPageTitle = () => {
        return t('parc.lookups.title') || 'Tables de Référence';
    };

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('common.actions.new') || 'Nouveau'}
        </Button>
    );

    return (
        <GenericPage title={getPageTitle()} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="Reference tables tabs">
                    <Tab label={t('parc.lookups.proprietors')} value="proprietaires" />
                    <Tab label={t('parc.lookups.fuelTypes')} value="fuelTypes" />
                    <Tab label={t('parc.lookups.affectations')} value="affectations" />
                    <Tab label={t('parc.lookups.docTypes')} value="docTypes" />
                    <Tab label={t('parc.lookups.mntTypes')} value="mntTypes" />
                </Tabs>
            </Box>

            <GenericTable
                columns={columns}
                data={items}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingItem ? t('common.actions.edit') : t('common.actions.new')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('page.parameters.table.code') || 'Code'}
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                required
                                inputProps={{ maxLength: 12 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('page.parameters.table.label') || 'Libellé'}
                                value={formData.lib}
                                onChange={(e) => setFormData({ ...formData, lib: e.target.value })}
                                required
                                inputProps={{ maxLength: 250 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Lookups;
